import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';
import { getToken } from './authService';

// User service for managing Entra ID user information

// User cache for session storage
const USER_CACHE_KEY = 'pulsecrm_user_cache';
const CACHE_EXPIRY_HOURS = 24; // Increased to 24 hours since user info is relatively static
const USE_LOCAL_STORAGE = true; // Use localStorage for longer persistence

// User JSON Schema
const createEmptyUser = () => ({
  userId: '',
  email: '',
  displayName: '',
  roles: [],
  preferences: {},
  createdBy: '',
  createdDate: '',
  modifiedBy: '',
  modifiedDate: ''
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

class UserService {
  constructor() {
    this.userCache = this.loadCacheFromSession();
    this.cacheStats = {
      hits: 0,
      misses: 0,
      apiCalls: 0
    };
  }

  // Get cache storage (localStorage or sessionStorage)
  getCacheStorage() {
    return USE_LOCAL_STORAGE ? localStorage : sessionStorage;
  }

  // Load user cache from storage
  loadCacheFromSession() {
    try {
      const storage = this.getCacheStorage();
      const cached = storage.getItem(USER_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        
        // Handle both old and new cache formats
        let users = {};
        if (data.users) {
          // New format with expiry
          if (new Date(data.expiry) > new Date()) {
            users = data.users;
          } else {
            console.log('User cache expired, clearing...');
            storage.removeItem(USER_CACHE_KEY);
            return {};
          }
        } else {
          // Old format - direct user objects
          users = data;
        }
        
        // Remove any cached photo URLs since they're invalid blob URLs
        Object.keys(users).forEach(email => {
          if (users[email] && typeof users[email] === 'object') {
            delete users[email].photoUrl;
          }
        });
        
        console.log(`Loaded ${Object.keys(users).length} users from cache`);
        return users;
      }
    } catch (error) {
      console.warn('Error loading user cache from session:', error);
    }
    return {};
  }

  // Save user cache to storage
  saveCacheToSession() {
    try {
      const cacheData = {
        users: this.userCache,
        expiry: new Date(Date.now() + CACHE_EXPIRY_HOURS * 60 * 60 * 1000).toISOString(),
        lastUpdated: new Date().toISOString(),
        version: '1.0'
      };
      this.getCacheStorage().setItem(USER_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error saving user cache:', error);
    }
  }

  // Get cache statistics
  getCacheStats() {
    const totalUsers = Object.keys(this.userCache).length;
    const hitRate = this.cacheStats.hits + this.cacheStats.misses > 0 
      ? (this.cacheStats.hits / (this.cacheStats.hits + this.cacheStats.misses) * 100).toFixed(1)
      : 0;
    
    return {
      ...this.cacheStats,
      totalCachedUsers: totalUsers,
      hitRate: `${hitRate}%`,
      storage: USE_LOCAL_STORAGE ? 'localStorage' : 'sessionStorage',
      cacheExpiry: `${CACHE_EXPIRY_HOURS} hours`
    };
  }

  // Get user display information by email
  async getUserByEmail(email) {
    if (!email) return null;

    // Check cache first (but not for photo URLs)
    if (this.userCache[email]) {
      this.cacheStats.hits++;
      const cachedUser = { ...this.userCache[email] };
      
      // Always fetch photo fresh since blob URLs can't be cached
      if (cachedUser.email) {
        try {
          cachedUser.photoUrl = await this.fetchUserPhoto(email);
        } catch (error) {
          console.warn(`Error fetching fresh photo for ${email}:`, error);
          cachedUser.photoUrl = null;
        }
      }
      
      return cachedUser;
    }

    try {
      this.cacheStats.misses++;
      // Try to get user info from API
      const userInfo = await this.fetchUserFromApi(email);
      if (userInfo) {
        // Cache the user info (without photo URL)
        const cacheableUser = { ...userInfo };
        delete cacheableUser.photoUrl; // Don't cache photo URLs
        this.userCache[email] = cacheableUser;
        this.saveCacheToSession();
        return userInfo;
      }
    } catch (error) {
      console.warn(`Error fetching user info for ${email}:`, error);
    }

    // Fallback: create basic user info from email
    const fallbackUser = {
      email: email,
      name: this.getNameFromEmail(email),
      photoUrl: null,
      initials: this.getInitialsFromEmail(email)
    };

    // Cache the fallback info
    this.userCache[email] = fallbackUser;
    this.saveCacheToSession();
    
    return fallbackUser;
  }

  // Separate method to fetch user photo
  async fetchUserPhoto(email) {
    try {
      const accessToken = await getToken();
      const response = await fetch(`https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}/photo/$value`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      if (response.ok) {
        const photoBlob = await response.blob();
        return URL.createObjectURL(photoBlob);
      }
      return null;
    } catch (error) {
      console.warn('Could not fetch user photo from Microsoft Graph:', error);
      return null;
    }
  }

  // Get multiple users by email array
  async getUsersByEmails(emails) {
    if (!emails || !Array.isArray(emails)) return [];

    const users = [];
    for (const email of emails) {
      const user = await this.getUserByEmail(email);
      if (user) {
        users.push(user);
      }
    }
    return users;
  }

  // Get user display information by email using Microsoft Graph directly
  async fetchUserFromApi(email) {
    try {
      this.cacheStats.apiCalls++;
      const accessToken = await getToken();
      
      // Call Microsoft Graph directly
      const response = await fetch(`https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user from Graph: ${response.status}`);
      }

      const userData = await response.json();
      const photoUrl = await this.fetchUserPhoto(email);

      return {
        email: userData.mail || userData.userPrincipalName,
        name: userData.displayName,
        firstName: userData.givenName,
        lastName: userData.surname,
        photoUrl: photoUrl,
        initials: this.getInitials(userData.givenName, userData.surname)
      };
    } catch (error) {
      console.error('Error fetching user from Microsoft Graph:', error);
      throw error;
    }
  }

  // Helper method to get name from email
  getNameFromEmail(email) {
    if (!email) return '';
    const parts = email.split('@')[0].split('.');
    return parts
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  // Helper method to get initials from email
  getInitialsFromEmail(email) {
    if (!email) return '';
    const parts = email.split('@')[0].split('.');
    return parts.map(part => part.charAt(0).toUpperCase()).join('');
  }

  // Helper method to get initials from name parts
  getInitials(firstName, lastName) {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}`;
  }

  // Clear the user cache
  clearCache() {
    this.userCache = {};
    this.getCacheStorage().removeItem(USER_CACHE_KEY);
  }

  // Log cache statistics
  logCacheStats() {
    console.log('User Cache Statistics:', this.getCacheStats());
  }

  // Preload multiple users into cache
  async preloadUsers(emails) {
    if (!emails || !Array.isArray(emails)) return;

    const uniqueEmails = [...new Set(emails)];
    const uncachedEmails = uniqueEmails.filter(email => !this.userCache[email]);

    if (uncachedEmails.length === 0) {
      return;
    }

    try {
      await Promise.all(uncachedEmails.map(email => this.getUserByEmail(email)));
    } catch (error) {
      console.error('Error preloading users:', error);
    }
  }
}

// Create singleton instance
const userService = new UserService();
export default userService;

// Export API methods
export const userServiceApi = {
  // Create empty user
  createEmptyUser,

  // Get all users
  async getUsers() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/users`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  },

  // Get user by ID
  async getUser(userId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/users/${userId}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  },

  // Get user by email
  async getUserByEmail(email) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/users/email/${encodeURIComponent(email)}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting user by email:', error);
      throw error;
    }
  },

  // Save user (create or update)
  async saveUser(user, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const isNew = !user.userId;
      
      // For new users, generate ID and set created info
      if (isNew) {
        user.userId = uuidv4();
        user.createdBy = currentUserEmail;
        user.createdDate = new Date().toISOString();
      } else {
        user.modifiedBy = currentUserEmail;
        user.modifiedDate = new Date().toISOString();
      }

      const method = isNew ? 'POST' : 'PUT';
      const url = isNew 
        ? `${apiBaseUrl}/users`
        : `${apiBaseUrl}/users/${user.userId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(user)
      });

      return handleResponse(response);
    } catch (error) {
      console.error('Error saving user:', error);
      throw error;
    }
  },

  // Delete user
  async deleteUser(userId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/users/${userId}`, {
        method: 'DELETE'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  // Update user roles
  async updateUserRoles(userId, roles, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/users/${userId}/roles`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roles,
          modifiedBy: currentUserEmail,
          modifiedDate: new Date().toISOString()
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating user roles:', error);
      throw error;
    }
  },

  // Update user preferences
  async updateUserPreferences(userId, preferences, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/users/${userId}/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preferences,
          modifiedBy: currentUserEmail,
          modifiedDate: new Date().toISOString()
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  }
}; 